<script setup lang="ts">
import { computed, inject, onMounted, ref, shallowRef } from "vue";
import { useRoute } from "vue-router";

import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TextInput from "@/components/ui/TextInput.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { getUserFriendlyName, type UserDto, type WorkspaceDto } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { formatDatetime } from "@/services/utils";
import { useModalsStore } from "@/stores/modals";
import { useToastsStore } from "@/stores/toasts";

const api = useApi();
const route = useRoute();
const modals = useModalsStore();
const toasts = useToastsStore();

const raiseError = inject(raiseErrorKey);

const isLoading = ref(true);
const isLoadingMembers = ref(true);
const workspace = ref<WorkspaceDto>();
const members = shallowRef(new Map<string, UserDto>());

const workspaceId = computed(() => parseInt(route.params.workspaceId as string));

async function fetchWorkspace() {
  isLoading.value = true;
  const t = await api.fetchWorkspace(workspaceId.value);
  if (t !== null) {
    workspace.value = t;
  } else {
    raiseError?.(404);
  }
  isLoading.value = false;
}

async function fetchMembers() {
  isLoadingMembers.value = true;
  if (workspace.value && workspace.value.members) {
    const idsToFetch = new Set<string>();
    workspace.value.members.forEach((m) => {
      idsToFetch.add(m.user_id);
      if (m.invited_by !== undefined && m.invited_by !== null) {
        idsToFetch.add(m.invited_by);
      }
    });
    const results = new Map<string, UserDto>();
    for (const id of idsToFetch) {
      const u = await api.fetchUser(id);
      if (u) {
        results.set(id, u);
      }
    }
    members.value = results;
  }
  isLoadingMembers.value = false;
}

function getMemberFriendlyName(member: UserDto) {
  return getUserFriendlyName(member);
}

function getInviterFriendlyName(userId: string) {
  const inviter = members.value.get(userId);
  return inviter ? getMemberFriendlyName(inviter) : "Deleted user";
}

async function onInviteMember() {
  const [isConfirmed, email] = await modals.prompt({
    title: "Invite member",
    titleIcon: "icon-new-user",
    message: "Enter the email of the member you want to invite.",
    promptedValueName: "email",
    inputType: "email",
    inputPattern: "^[^@\s]+@[^@\s]+\.[^@\s]+$",
  });
  if (isConfirmed) {
    await api.inviteWorkspaceMembers(workspaceId.value, [email]);
    toasts.addToast("Invitation sent", "success", { duration: 5 });
  }
}

onMounted(async () => {
  await fetchWorkspace();
  await fetchMembers();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      { label: 'Workspaces', to: { name: RouteNames.Dispatch } },
      { label: `${workspace?.name} settings` },
    ]"
  >
    <div class="workspace-detail-view">
      <Transition name="fade">
        <SkoreSpinner v-if="isLoading" />
        <div v-else-if="workspace !== undefined" class="wrapper">
          <header>
            <h1 class="text-2xl-medium">{{ workspace.name }}</h1>
            <h2 class="text-base-regular">Manage workspace settings.</h2>
          </header>

          <div class="profile-element">
            <div>Public ID</div>
            <TextInput v-model="workspace.public_id" disabled />
          </div>

          <div class="profile-element">
            <h1 class="text-2xl-medium">Name</h1>
            <TextInput v-model="workspace.name" disabled />
          </div>

          <header class="members">
            <div>
              <h1 class="text-2xl-medium">Members</h1>
              <h2 class="text-base-regular">Manage members inside your organisation.</h2>
            </div>

            <SimpleButton
              icon-left="icon-new-user"
              label="Invite member"
              kind="ghost"
              @click="onInviteMember"
            />
          </header>

          <SkoreSpinner v-if="isLoadingMembers" />
          <table v-else class="tabular-list members">
            <thead>
              <tr>
                <th class="text-base-medium">User</th>
                <th class="text-base-medium">Mail</th>
                <th class="text-base-medium">Joined</th>
                <th class="text-base-medium">Invited by</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="member in workspace.members" :key="member.user_id">
                <td class="text-base-regular">
                  <UserAvatar :name="getMemberFriendlyName(members.get(member.user_id)!)" />
                </td>
                <td class="text-base-regular">{{ members.get(member.user_id)!.email }}</td>
                <td class="text-base-regular">{{ formatDatetime(workspace.created_at) }}</td>
                <td class="text-base-regular">
                  {{ getInviterFriendlyName(member.invited_by ?? "") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Transition>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.workspace-detail-view {
  .back {
    color: var(--content-secondary);
    text-decoration: none;

    i {
      padding-right: var(--numbers-8);
      vertical-align: sub;
    }
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    padding: calc(var(--numbers-32) - var(--numbers-12)) 0 var(--numbers-32) 0;
    gap: var(--numbers-32);

    h1 {
      color: var(--content-primary);

      i {
        margin-right: var(--numbers-6);
        color: var(--content-tertiary);
      }
    }

    h2 {
      color: var(--color-text-secondary);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-regular);
    }

    header.members {
      display: flex;
      justify-content: space-between;
    }

    table.members {
      td {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}
</style>
